import { PatientWithStatsInhalerUsage } from '@/jsonapi/PatientWithStats'
import moment from 'moment-timezone'
import { removeAbbr, USER_TIME_ZONE } from '@/utils/time'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { DataPoint } from '@/jsonapi/CommonTypes'
import * as R from 'ramda'

export function groupByDay<T>(dataPoints, timezone): { [key: string]: T[] } {
  const dataPointsMapByDay = {}
  dataPoints.forEach(dataPoint => {
    const startOfDay = moment(dataPoint.timestamp)
      .tz(timezone || USER_TIME_ZONE)
      .startOf('day')
      .valueOf()
    if (!dataPointsMapByDay[startOfDay]) {
      dataPointsMapByDay[startOfDay] = []
    }
    dataPointsMapByDay[startOfDay].push(dataPoint)
  })
  return dataPointsMapByDay
}

type DataPointWithName = { name: string } & DataPoint

export function buildRescueInhalerSeries(
  inhalerUsages: PatientWithStatsInhalerUsage[],
  timezone: string,
  threshold: number
): { seriesOption: echarts.EChartOption.SeriesBar; maxValue: number } {
  const rescueInhalerUsageDataPoints: DataPointWithName[] = inhalerUsages
    .map(usages => {
      // FIXME temporarily filter by timestamp in frontend
      return R.uniqBy(
        dp => dp.timestamp,
        usages.dataPoints
          .filter(point => point.value !== 0)
          .map(dataPoint => {
            return {
              ...dataPoint,
              name: usages.name,
            }
          })
      )
    })
    .flat(Infinity)

  const rescueInhalerUsageDataPointsGroupByDay = groupByDay<DataPointWithName>(rescueInhalerUsageDataPoints, timezone)

  const seriesData = Object.entries(rescueInhalerUsageDataPointsGroupByDay).map(([key, values]) => {
    const sum = values.reduce((acc, cur) => acc + cur.value, 0)
    return {
      value: [key, sum, values],
      itemStyle: {
        color: sum > threshold ? '#9a3f67' : '#5fd0d0',
      },
    }
  })

  const seriesOption = {
    type: 'bar',
    data: seriesData,
    itemStyle: {
      barBorderRadius: [4, 4, 0, 0],
    },
    markLine: {
      silent: true,
      precision: 0,
      symbol: [],
      label: {
        position: 'start',
      },
      lineStyle: {
        color: '#0f45cd',
        opacity: 0.2,
        type: 'solid',
      },
      data: [
        // @ts-ignore
        { yAxis: threshold },
      ],
    },
    tooltip: {
      formatter: params => {
        const data = params.value[params.value.length - 1]
        const usageLabels = data.map(d => {
          return `${d.name} ${d.value} times`
        })
        const dateLabel = moment(data[0].timestamp)
          .tz(timezone)
          .format(removeAbbr(timezone, CHART_DATE_FORMAT.DAY_ABBR))

        return `${dateLabel}<br>${usageLabels.join('<br>')}`
      },
    },
  }

  return {
    // @ts-ignore
    seriesOption,
    maxValue: Math.max(...rescueInhalerUsageDataPoints.map(d => d.value)),
  }
}
